import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  type AzureEntraUserPickerProps,
  type AzureEntraUser,
  type AzureEntraUserPickerUiOptions,
} from './schema';

export { AzureEntraUserPickerSchema } from './schema';

function getUiOptions(
  uiSchema: AzureEntraUserPickerProps['uiSchema'],
): AzureEntraUserPickerUiOptions {
  return uiSchema['ui:options'] ?? {};
}

function formatUserOption(user: AzureEntraUser): string {
  return user.displayName && user.displayName !== user.email
    ? `${user.displayName} (${user.email})`
    : user.email;
}

function getInputPlaceholder(
  placeholder: string | undefined,
  multiple: boolean,
): string {
  if (placeholder) {
    return placeholder;
  }
  return multiple
    ? 'Search by emails or display names'
    : 'Search by email or display name';
}

function isUserObject(value: unknown): value is AzureEntraUser {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as AzureEntraUser).email === 'string'
  );
}

// An empty `id` marks a user that was not resolved from Entra (for example a
// manually typed email). Such users are never returned in `object` mode.
function toUser(option: AzureEntraUser | string): AzureEntraUser | undefined {
  if (typeof option === 'string') {
    const email = option.trim();
    if (!email) {
      return undefined;
    }
    return { id: '', email, displayName: email };
  }

  const email = option.email?.trim() ?? '';
  if (!email) {
    return undefined;
  }

  const id =
    typeof option.id === 'string' && option.id.trim().length > 0
      ? option.id.trim()
      : '';
  const displayName =
    typeof option.displayName === 'string' &&
    option.displayName.trim().length > 0
      ? option.displayName.trim()
      : email;

  return { id, email, displayName };
}

function getRawFormValues(
  formData: AzureEntraUserPickerProps['formData'],
  multiple: boolean,
): Array<string | AzureEntraUser> {
  if (multiple) {
    return Array.isArray(formData) ? formData : [];
  }
  if (formData === undefined) {
    return [];
  }
  if (Array.isArray(formData)) return formData.slice(0, 1);
  return [formData];
}

const EMPTY_USER_OPTIONS: Array<AzureEntraUser | string> = [];

function canReturnUser(
  user: AzureEntraUser,
  returnFormat: 'email' | 'object',
): boolean {
  return returnFormat === 'email' || user.id !== '';
}

function toFormValue(
  user: AzureEntraUser,
  returnFormat: 'email' | 'object',
): string | AzureEntraUser {
  return returnFormat === 'object' ? user : user.email;
}

/**
 * The underlying component that is rendered in the form for the `AzureEntraUserPicker`
 * field extension.
 *
 * This implementation uses Azure Entra/Graph search via the backend endpoint and keeps
 * a local cache of discovered users so selected values remain visible after query reset.
 *
 * @public
 */
export const AzureEntraUserPicker = (props: AzureEntraUserPickerProps) => {
  const {
    onChange,
    schema: {
      title = 'Search by email or display name',
      description = 'Start typing to search for users',
    },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
  } = props;

  const uiOptions = getUiOptions(uiSchema);
  const isDisabled = uiSchema['ui:disabled'] ?? false;
  const multiple = uiOptions.multiple ?? false;
  const autoSelect = uiOptions.autoSelect ?? true;
  const minQueryLength = uiOptions.minQueryLength ?? 3;
  const maxOptions = uiOptions.maxOptions ?? 20;
  const debounceMs = uiOptions.debounceMs ?? 250;
  const returnFormat = uiOptions.returnValue === 'object' ? 'object' : 'email';
  // Object mode must carry a real Entra id, so free-text values are not allowed.
  const allowArbitraryValues =
    (uiOptions.allowArbitraryValues ?? false) && returnFormat === 'email';
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const entityRef = uiOptions.entityField
    ? (props.formContext?.formData?.[uiOptions.entityField] as
        | string
        | undefined)
    : undefined;
  const previousEntityRef = useRef<string | undefined>(undefined);
  const onChangeRef = useRef(onChange);
  const [knownUsersByEmail, setKnownUsersByEmail] = useState(
    () => new Map<string, AzureEntraUser>(),
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (previousEntityRef.current === entityRef) return undefined;
    previousEntityRef.current = entityRef;
    if (!uiOptions.entityField || !uiOptions.annotation || !entityRef) {
      return undefined;
    }

    let cancelled = false;
    // Clear a previous application's owner while the new entity is loading.
    onChangeRef.current(undefined);
    catalogApi
      .getEntityByRef(entityRef)
      .then(entity => {
        const email =
          entity?.metadata.annotations?.[uiOptions.annotation!]?.trim();
        if (cancelled || !email) return;

        const user = { id: '', email, displayName: email };
        setKnownUsersByEmail(previous => new Map(previous).set(email, user));
        onChangeRef.current(
          multiple || uiOptions.returnArray ? [email] : email,
        );
      })
      .catch(() => {
        // Keep the picker empty and available for manual Entra search.
      });

    return () => {
      cancelled = true;
    };
  }, [
    catalogApi,
    entityRef,
    multiple,
    uiOptions.annotation,
    uiOptions.entityField,
    uiOptions.returnArray,
  ]);

  const selectedUsers = useMemo(() => {
    return getRawFormValues(formData, multiple)
      .map(value =>
        typeof value === 'string' || isUserObject(value)
          ? toUser(value)
          : undefined,
      )
      .filter(
        (user): user is AzureEntraUser =>
          user !== undefined && canReturnUser(user, returnFormat),
      );
  }, [formData, multiple, returnFormat]);

  useEffect(() => {
    if (
      !uiOptions.defaultToCurrentUser ||
      multiple ||
      isDisabled ||
      selectedUsers.length > 0 ||
      (uiOptions.entityField && entityRef)
    ) {
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;

    const defaultToSignedInUser = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email?.trim();
        if (!email) return;

        const baseUrl = await discoveryApi.getBaseUrl(
          'scaffolder-field-extension-user',
        );
        const { token } = await identityApi.getCredentials();
        const url = new URL(`${baseUrl}/users/search`);
        url.searchParams.set('query', email);
        url.searchParams.set('limit', String(maxOptions));

        const response = await fetch(url.toString(), {
          signal: abortController.signal,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) return;

        const payload = (await response.json()) as {
          items?: Array<Partial<AzureEntraUser>>;
        };
        const match = payload.items?.find(
          candidate =>
            typeof candidate.id === 'string' &&
            candidate.id.trim().length > 0 &&
            typeof candidate.email === 'string' &&
            candidate.email.trim().toLowerCase() === email.toLowerCase(),
        );
        if (!match || !isActive) return;

        const user: AzureEntraUser = {
          id: match.id!.trim(),
          email: match.email!.trim(),
          displayName: match.displayName?.trim() || email,
        };

        setKnownUsersByEmail(previous =>
          new Map(previous).set(user.email, user),
        );
        if (uiOptions.returnArray) {
          onChangeRef.current(
            returnFormat === 'object' ? [user] : [user.email],
          );
        } else {
          onChangeRef.current(toFormValue(user, returnFormat));
        }
      } catch {
        // Leave the field empty if the signed-in user cannot be resolved.
      }
    };

    defaultToSignedInUser();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [
    discoveryApi,
    entityRef,
    identityApi,
    isDisabled,
    maxOptions,
    multiple,
    returnFormat,
    selectedUsers.length,
    uiOptions.defaultToCurrentUser,
    uiOptions.entityField,
    uiOptions.returnArray,
  ]);

  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [options, setOptions] = useState<AzureEntraUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);

  // Initialize known users cache with selected values from formData
  useEffect(() => {
    if (selectedUsers.length === 0) {
      return;
    }

    setKnownUsersByEmail(prev => {
      const next = new Map(prev);
      let changed = false;

      for (const user of selectedUsers) {
        const existing = next.get(user.email);
        if (!existing) {
          next.set(user.email, user);
          changed = true;
          continue;
        }

        const merged: AzureEntraUser = {
          id: user.id || existing.id,
          email: user.email,
          displayName:
            user.displayName !== user.email
              ? user.displayName
              : existing.displayName,
        };
        if (
          merged.id !== existing.id ||
          merged.displayName !== existing.displayName
        ) {
          next.set(user.email, merged);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [selectedUsers]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, debounceMs);

    return () => clearTimeout(timeout);
  }, [inputValue, debounceMs]);

  useEffect(() => {
    let isActive = true;
    const query = debouncedInput.trim();

    if (!query && minQueryLength > 0) {
      setOptions([]);
      setSearchError(undefined);
      setIsSearching(false);
      return undefined;
    }

    if (query.length > 0 && query.length < minQueryLength) {
      setOptions([]);
      setSearchError(undefined);
      setIsSearching(false);
      return undefined;
    }

    const abortController = new AbortController();

    const searchUsers = async () => {
      setIsSearching(true);
      setSearchError(undefined);

      try {
        const baseUrl = await discoveryApi.getBaseUrl(
          'scaffolder-field-extension-user',
        );
        const { token } = await identityApi.getCredentials();
        const url = new URL(`${baseUrl}/users/search`);
        url.searchParams.set('query', query);
        url.searchParams.set('limit', String(maxOptions));

        const response = await fetch(url.toString(), {
          signal: abortController.signal,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!response.ok) {
          throw new Error(`User search failed with status ${response.status}`);
        }

        const payload = (await response.json()) as {
          items?: Array<Partial<AzureEntraUser>>;
        };

        const users = Array.isArray(payload.items)
          ? payload.items
              .map(item => {
                const email =
                  typeof item.email === 'string' ? item.email.trim() : '';
                if (!email) {
                  return null;
                }

                const id =
                  typeof item.id === 'string' && item.id.trim().length > 0
                    ? item.id
                    : '';
                const displayName =
                  typeof item.displayName === 'string' &&
                  item.displayName.trim().length > 0
                    ? item.displayName
                    : email;

                return { id, email, displayName };
              })
              .filter(
                (item): item is AzureEntraUser =>
                  item !== null && canReturnUser(item, returnFormat),
              )
              .slice(0, maxOptions)
          : [];

        if (isActive) {
          setOptions(users);
          setKnownUsersByEmail(prev => {
            const next = new Map(prev);
            for (const user of users) {
              next.set(user.email, user);
            }
            return next;
          });
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }

        if (isActive) {
          setOptions([]);
          setSearchError('Unable to load users from Azure Entra right now.');
        }
      } finally {
        if (isActive) {
          setIsSearching(false);
        }
      }
    };

    searchUsers();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [
    debouncedInput,
    discoveryApi,
    identityApi,
    maxOptions,
    minQueryLength,
    returnFormat,
  ]);

  const singleValue = useMemo<AzureEntraUser | string | null>(() => {
    if (multiple) {
      return null;
    }

    const selected = selectedUsers[0];
    if (!selected) {
      return null;
    }

    const matched = knownUsersByEmail.get(selected.email);
    if (matched) {
      return matched;
    }

    return allowArbitraryValues ? selected.email : null;
  }, [multiple, selectedUsers, knownUsersByEmail, allowArbitraryValues]);

  const multipleValue = useMemo<Array<AzureEntraUser | string>>(() => {
    if (!multiple || selectedUsers.length === 0) {
      return EMPTY_USER_OPTIONS;
    }

    return selectedUsers
      .map(
        user =>
          knownUsersByEmail.get(user.email) ??
          (allowArbitraryValues ? user.email : null),
      )
      .filter((value): value is AzureEntraUser | string => value !== null);
  }, [multiple, selectedUsers, knownUsersByEmail, allowArbitraryValues]);

  const effectiveOptions = useMemo(() => {
    const selectedObjects = (multiple ? multipleValue : [singleValue]).filter(
      (value): value is AzureEntraUser =>
        typeof value !== 'string' && value !== null,
    );

    const unique = new Map<string, AzureEntraUser>();
    for (const user of selectedObjects) {
      unique.set(user.email, user);
    }
    for (const user of options) {
      unique.set(user.email, user);
    }

    return Array.from(unique.values());
  }, [multiple, multipleValue, singleValue, options]);

  useEffect(() => {
    if (!autoSelect || !required || multiple || isDisabled) {
      return;
    }

    if (!formData && options.length === 1) {
      onChange(toFormValue(options[0], returnFormat));
    }
  }, [
    autoSelect,
    required,
    multiple,
    isDisabled,
    formData,
    options,
    onChange,
    returnFormat,
  ]);

  const loading =
    isSearching ||
    (inputValue.length >= minQueryLength && inputValue !== debouncedInput);

  let helperText = uiSchema['ui:description'] ?? description;
  if (searchError) {
    helperText = searchError;
  }
  if (rawErrors && rawErrors.length > 0) {
    helperText = rawErrors.join(', ');
  }

  return (
    <Box>
      <Autocomplete<AzureEntraUser, boolean, false, boolean>
        id={idSchema?.$id}
        multiple={multiple}
        freeSolo={allowArbitraryValues}
        openOnFocus
        forcePopupIcon
        options={effectiveOptions}
        value={multiple ? multipleValue : singleValue}
        loading={loading}
        disabled={isDisabled}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        onChange={(_, value) => {
          if (multiple) {
            const users = (value as Array<AzureEntraUser | string>)
              .map(toUser)
              .filter(
                (user): user is AzureEntraUser =>
                  user !== undefined && canReturnUser(user, returnFormat),
              );
            onChange(
              returnFormat === 'object' ? users : users.map(user => user.email),
            );
            return;
          }

          if (value === null || Array.isArray(value)) {
            onChange(undefined);
            return;
          }

          const user = toUser(value);
          if (!user || !canReturnUser(user, returnFormat)) {
            onChange(undefined);
            return;
          }

          if (returnFormat === 'object') {
            onChange(uiOptions.returnArray ? [user] : user);
          } else {
            onChange(uiOptions.returnArray ? [user.email] : user.email);
          }
        }}
        getOptionLabel={option =>
          typeof option === 'string' ? option : formatUserOption(option)
        }
        getOptionSelected={(option, value) => {
          if (typeof value === 'string') {
            return option.email === value;
          }
          return option.email === value.email;
        }}
        filterOptions={candidates =>
          inputValue.trim()
            ? candidates.filter(candidate =>
                options.some(option => option.email === candidate.email),
              )
            : candidates
        }
        noOptionsText={
          inputValue.trim().length < minQueryLength
            ? `Type at least ${minQueryLength} characters`
            : 'No matching users'
        }
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            variant="outlined"
            required={required}
            error={Boolean(rawErrors && rawErrors.length > 0)}
            placeholder={getInputPlaceholder(uiOptions.placeholder, multiple)}
            helperText={helperText}
            inputProps={params.inputProps}
          />
        )}
      />

      {inputValue.trim().length > 0 &&
      inputValue.trim().length < minQueryLength ? (
        <Typography variant="caption" color="textSecondary">
          Type at least {minQueryLength} characters to search Entra users.
        </Typography>
      ) : null}
    </Box>
  );
};
